import { useEffect, useState, useContext } from "react";
import { Link } from "react-router-dom";
import api from "../../../utils/axiosInstance";
import { AuthContext } from "../../../context/AuthContext";
import { JOB_STATUSES, jobStatusStyle } from "../../../utils/constants/jobStatus";
import Panel from "../../../components/Dashboard/Panel";
import StatTile from "../../../components/Dashboard/StatTile";
import StatusBreakdown from "../../../components/Dashboard/StatusBreakdown";
import DataTable from "../../../components/Dashboard/DataTable";
import Badge from "../../../components/Dashboard/Badge";
import PageHeader from "../../../components/Dashboard/PageHeader";
import {
  countBy,
  daysUntil,
  displayNameOf,
  dueLabel,
  fmtDate,
  greetingFor,
} from "../../../components/Dashboard/dashboardKit";
import { Briefcase, Clock, CheckCircle2, XCircle } from "lucide-react";
import { btn } from "../../../components/Button/buttonStyles";

const ACTIVE_STATUSES = ["Offer Accepted", "Started"];
const DONE_STATUSES = ["Completed", "Completion Accepted"];
const CLOSED_STATUSES = ["Offer Rejected", "Completion Rejected", "Cancelled"];

const DueCell = ({ value }) => {
  if (!value) return <span className="text-sm text-gray-400">—</span>;
  const days = daysUntil(value);
  return (
    <span className="flex items-center gap-2">
      <span className="text-sm text-gray-500">{fmtDate(value)}</span>
      <Badge className={days < 0 ? "bg-rose-50 text-rose-600" : days <= 2 ? "bg-amber-50 text-amber-700" : "bg-gray-100 text-gray-600"}>
        {dueLabel(days)}
      </Badge>
    </span>
  );
};

const VendorDashboard = () => {
  const { user } = useContext(AuthContext);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/vendor-jobs")
      .then((res) => setJobs(res.data?.data ?? []))
      .catch((err) => console.error("VendorDashboard fetch error:", err))
      .finally(() => setLoading(false));
  }, []);

  const pending = jobs.filter((j) => j.status === "Offered to Vendor");
  const active = jobs.filter((j) => ACTIVE_STATUSES.includes(j.status));
  const completed = jobs.filter((j) => DONE_STATUSES.includes(j.status));
  const closed = jobs.filter((j) => CLOSED_STATUSES.includes(j.status));

  const breakdown = countBy(jobs, "status", JOB_STATUSES).map(({ key, count }) => ({
    label: key,
    count,
    dot: jobStatusStyle(key).dot,
  }));

  const jobLink = (j) => (
    <Link
      to={`/vendor/job/${j.id}`}
      className="block max-w-[13rem] truncate text-sm font-medium text-gray-800 hover:text-brand-600"
    >
      {j.name}
    </Link>
  );

  const statusCell = (j) => (
    <Badge className={jobStatusStyle(j.status).badge}>{j.status}</Badge>
  );

  return (
    <div className="space-y-5">
      <PageHeader
        title="Dashboard"
        subtitle={`${greetingFor(displayNameOf(user, "there"))}. Your jobs at a glance.`}
      >
        <Link
          to="/jobs"
          className={btn("primary", "sm")}
        >
          <Briefcase size={14} />
          All jobs
        </Link>
      </PageHeader>

      <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
        <StatTile icon={Briefcase}   label="Pending offers"       value={loading ? null : pending.length}   tone="brand"   loading={loading} />
        <StatTile icon={Clock}       label="Active jobs"          value={loading ? null : active.length}    tone="amber"   loading={loading} />
        <StatTile icon={CheckCircle2} label="Completed"           value={loading ? null : completed.length} tone="emerald" loading={loading} />
        <StatTile icon={XCircle}     label="Rejected / cancelled" value={loading ? null : closed.length}    tone="rose"    loading={loading} />
      </div>

      <div className="grid gap-4 xl:grid-cols-3">
        <Panel
          className="xl:col-span-2"
          title="Action required"
          subtitle="Jobs offered to you that need a response"
          action={
            pending.length > 0 && (
              <span className="rounded-full bg-brand-600 px-2.5 py-1 text-[11px] font-bold text-white">
                {pending.length}
              </span>
            )
          }
        >
          <DataTable
            loading={loading}
            rows={pending}
            empty="No pending offers right now."
            columns={[
              { key: "name", header: "Job", cell: jobLink },
              {
                key: "service",
                header: "Service",
                className: "hidden sm:table-cell",
                cell: (j) => <span className="text-sm text-gray-500">{j.service?.name ?? "—"}</span>,
              },
              { key: "due", header: "Deadline", cell: (j) => <DueCell value={j.deadline_at} /> },
              { key: "status", header: "Status", cell: statusCell },
            ]}
          />
        </Panel>

        <Panel
          title="All jobs by status"
          subtitle={loading ? null : `${jobs.length} jobs assigned to you`}
        >
          <StatusBreakdown items={breakdown} loading={loading} empty="No jobs assigned yet." />
        </Panel>

        <Panel
          className="xl:col-span-3"
          title="In progress"
          subtitle="Jobs you are currently working on"
          action={
            <Link to="/jobs" className="text-xs font-semibold text-brand-600 hover:text-brand-700 hover:underline">
              View all jobs
            </Link>
          }
        >
          <DataTable
            loading={loading}
            rows={active}
            empty="No jobs in progress."
            columns={[
              { key: "name", header: "Job", cell: jobLink },
              {
                key: "pair",
                header: "Language pair",
                className: "hidden sm:table-cell",
                cell: (j) => (
                  <span className="text-sm text-gray-500">
                    {j.languagePair
                      ? `${j.languagePair.sourceLanguage?.name ?? "?"} → ${j.languagePair.targetLanguage?.name ?? "?"}`
                      : "—"}
                  </span>
                ),
              },
              { key: "due", header: "Deadline", cell: (j) => <DueCell value={j.deadline_at} /> },
              { key: "status", header: "Status", cell: statusCell },
            ]}
          />
        </Panel>
      </div>
    </div>
  );
};

export default VendorDashboard;
